import { FC, useMemo } from 'react';
import { PoiTableFragment } from '../../../generated/types';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import {
  Box,
  Flex,
  Icon,
  Img,
  StackDivider,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import Link from 'next/link';

interface ListProps {
  pois: PoiTableFragment[];
}

const List: FC<ListProps> = ({ pois }) => {
  const groupedData = useMemo(() => {
    const groups = groupBy(pois, (item) => {
      return item.country?.id || 0;
    });

    const groupedData = [];

    for (const [countryId, poisInCountry] of Object.entries(groups)) {
      const groupData = {
        country: poisInCountry[0].country,
        regions: [],
      };

      const groupedRegions = groupBy(
        poisInCountry,
        (item) => item.region?.id || 0
      );

      for (let [regionId, poisInRegion] of Object.entries(groupedRegions)) {
        if (poisInRegion.length === 0) {
          continue;
        }

        const regionData = {
          region: poisInRegion[0].region,
          pois: sortBy(poisInRegion, (item) => item.title),
        };

        groupData.regions.push(regionData);
      }

      // check if we have no regions
      if (groupData.regions.length > 0) {
        groupData.regions = sortBy(
          groupData.regions,
          (regionData) => regionData.region?.name || 0
        );
        groupedData.push(groupData);
      }
    }

    return sortBy(groupedData, (group) => group.country?.name || 0);
  }, [pois]);

  return (
    <Box>
      {groupedData.map((countryGroup) => {
        return (
          <Box
            key={countryGroup.country?.id || 0}
            my={6}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Flex
              px={2}
              fontSize="xl"
              fontWeight="bold"
              py={2}
              alignItems="center"
              bg="gray.100"
              borderBottomWidth="1px"
              borderBottomColor="gray.200"
            >
              {countryGroup.country?.isocode && (
                <Img
                  src={`/bikerbetten/flags/${countryGroup.country.isocode.toLowerCase()}.svg`}
                  width="20px"
                  mr={2}
                />
              )}
              {countryGroup.country?.name || 'Global'}
            </Flex>

            <VStack spacing={1} alignItems="flex-start">
              {countryGroup.regions.map((regionGroup) => {
                return (
                  <Box key={regionGroup.region?.id || 0} width="100%">
                    {regionGroup.region && (
                      <Box
                        py={1}
                        px={2}
                        fontWeight="bold"
                        fontSize="md"
                        color="gray.600"
                        borderBottomWidth="1px"
                        borderTopWidth="1px"
                        borderColor="gray.200"
                        bg="gray.100"
                      >
                        {regionGroup.region?.name}
                      </Box>
                    )}

                    <VStack
                      spacing={0}
                      alignItems="flex-start"
                      divider={<StackDivider borderColor="gray.100" />}
                      width="100%"
                    >
                      {regionGroup.pois.map((poi) => {
                        return (
                          <Link passHref href={poi.url}>
                            <Flex
                              as="a"
                              key={poi.id}
                              p={2}
                              width="100%"
                              _hover={{
                                bg: 'gray.100',
                              }}
                              cursor="pointer"
                              alignItems="center"
                            >
                              <Box flex={1}>
                                <Box fontWeight="bold">{poi.title}</Box>
                                <Wrap>
                                  <WrapItem>
                                    {poi.location && (
                                      <Flex
                                        mt={1}
                                        fontSize="xs"
                                        fontWeight="default"
                                        textTransform="initial"
                                        bg="gray.100"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="sm"
                                      >
                                        <Box
                                          fontWeight="bold"
                                          borderRightWidth="1px"
                                          borderRightColor="gray.200"
                                          px={1}
                                          bg="gray.200"
                                        >
                                          Geo
                                        </Box>
                                        <Box px={1}>
                                          {poi.location.lat?.toFixed(6)},{' '}
                                          {poi.location.lng?.toFixed(6)}
                                        </Box>
                                      </Flex>
                                    )}
                                  </WrapItem>

                                  {poi.passHeight && (
                                    <WrapItem>
                                      <Flex
                                        mt={1}
                                        fontSize="xs"
                                        fontWeight="default"
                                        textTransform="initial"
                                        bg="gray.100"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="sm"
                                      >
                                        <Box
                                          fontWeight="bold"
                                          borderRightWidth="1px"
                                          borderRightColor="gray.200"
                                          px={1}
                                          bg="gray.200"
                                        >
                                          Höhe
                                        </Box>
                                        <Box px={1}>{poi.passHeight}</Box>
                                      </Flex>
                                    </WrapItem>
                                  )}

                                  <WrapItem>
                                    <Flex
                                      mt={1}
                                      fontSize="xs"
                                      fontWeight="default"
                                      textTransform="initial"
                                      bg="gray.100"
                                      border="1px solid"
                                      borderColor="gray.200"
                                      borderRadius="sm"
                                    >
                                      <Box
                                        fontWeight="bold"
                                        borderRightWidth="1px"
                                        borderRightColor="gray.200"
                                        px={1}
                                        bg="gray.200"
                                      >
                                        Maut
                                      </Box>
                                      <Box px={1}>
                                        {poi.toll ? 'Ja' : 'Nein'}
                                      </Box>
                                    </Flex>
                                  </WrapItem>

                                  <WrapItem>
                                    <Flex
                                      mt={1}
                                      fontSize="xs"
                                      fontWeight="default"
                                      textTransform="initial"
                                      bg="gray.100"
                                      border="1px solid"
                                      borderColor="gray.200"
                                      borderRadius="sm"
                                    >
                                      <Box
                                        fontWeight="bold"
                                        borderRightWidth="1px"
                                        borderRightColor="gray.200"
                                        px={1}
                                        bg="gray.200"
                                      >
                                        Belag
                                      </Box>
                                      <Box px={1}>
                                        {poi.unpaved
                                          ? 'Unbefestigt'
                                          : 'Geteert'}
                                      </Box>
                                    </Flex>
                                  </WrapItem>
                                </Wrap>
                              </Box>
                              <Box>
                                <Icon fontSize="2xl" as={ChevronRightIcon} />
                              </Box>
                            </Flex>
                          </Link>
                        );
                      })}
                    </VStack>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        );
      })}
    </Box>
  );
};

export default List;
